import React, { memo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Paper, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import EchelonsOverview from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/EchelonsOverview/EchelonsOverview';
import {
    useGetHrSimulationPayConstants,
    usePutHrSimulationPayConstants,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/helpers/hooks/payConstants';
import { pick } from 'lodash';
import {
    CostPerYearT,
    SmicTotalData,
    SmicValuesPerYear,
    useGetHrGVTData,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/GVTData/helpers/hooks/gvtData';
import { ApplyChangesButton } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ApplyChangesButton';
import {
    HrSimulationPayConstantPatchPayload,
    HrSimulationPayConstant,
    HypothesisKey,
    ValueKey,
    UserChanges,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/helpers/types';
import {
    DATE_KEY,
    validateUserChanges,
    VALUE_KEY,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/helpers/dataHelpers';
import HrCard from 'routes/HumanResourcesModule/components/HrCard';
import IndexRaiseHypothesisEntryForm from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/PayConstantsEntryForm/IndexRaiseHypothesisEntryForm';
import { SimulationTypeEnum } from 'routes/HumanResourcesModule/Simulation/helpers/constants';

import {
    useGetHrFreeHypotheses,
    useFreeHypothesisCRUDMutations,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/hooks/freeHypothesis';
import { FreeHypothesisEditMutationParams } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/types';
import FreeHypothesisEntryForm from './components/FreeHypothesis/FreeHypothesisEntryForm';
import HypothesisEntryForm from './components/PayConstantsEntryForm/HypothesisEntryForm';
import SmicHypothesisEntryForm from './components/PayConstantsEntryForm/SmicHypothesisEntryForm';

type Props = {
    hrSimulation: HrSimulation;
};

export const StyPaper = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.primary.background,
    height: '100%',
}));

const StyHrCard = styled(HrCard)(() => ({
    '.MuiCardHeader-action': { mr: 0 },
    height: '100%',
}));

const PayConstants = ({ hrSimulation }: Props): React.ReactElement => {
    const { t } = useTranslation();

    const [freeHypothesisUpdatePayload, setFreeHypothesisUpdatePayload] =
        useState<FreeHypothesisEditMutationParams[]>([]);
    const {
        data: hrSimulationPayConstantResponse,
        isLoading: isHrSimulationPayConstantLoading,
    } = useGetHrSimulationPayConstants(hrSimulation.id);

    const {
        data: gvtDataResponse,
        isFetching: isHrSimulationResponseFetching,
    } = useGetHrGVTData(hrSimulation.id);
    const { data: freeHypothesisResponse, isLoading: isFreeHypothesisLoading } =
        useGetHrFreeHypotheses(hrSimulation.id);
    const freeHypothesisData = freeHypothesisResponse ?? [];
    const [changedHypotheses, setChangedHypotheses] = useState<string[]>([]);

    const {
        smicTotalCost = {
            indiciaireCost: 0,
            nonIndiciaireCost: 0,
            totalCost: 0,
        },
        indexPointTotalCost = 0,
        indexRaiseTotalCost = 0,
        rateCnraclTotalCost = 0,
        echelonTotalCost = 0,
        freeHypothesisTotals = {},
        smicPerYear = {
            indiciaireCost: {},
            nonIndiciaireCost: {},
            perYearCost: {},
        },
        indexPointPerYear = {},
        indexRaisePerYear = {},
        rateCnraclPerYear = {},
        echelonPerYear = {},
        freeHypothesisPerYear = {},
    } = gvtDataResponse ?? {};

    const putPayConstantMutation = usePutHrSimulationPayConstants(
        hrSimulation.id,
        changedHypotheses,
        freeHypothesisUpdatePayload,
    );
    const { isPending: isLoadingPayConstantMutation } = putPayConstantMutation;

    const {
        indexPointHypothesis,
        indexRaiseHypothesis,
        rateCnraclHypothesis,
        smicHypothesis,
    } = hrSimulationPayConstantResponse ?? {};

    // if the simulation is a projection, the echelon overview disposal change and is displayed
    // in the same grid as the other hypotheses
    const isProjection = hrSimulation.type === SimulationTypeEnum.PROJECTION;

    const pickHypothesis = (
        hypothesis: HrSimulationPayConstant | undefined,
    ) => ({
        smicHypothesis: pick(hypothesis?.smicHypothesis, [VALUE_KEY, DATE_KEY]),
        indexPointHypothesis: pick(hypothesis?.indexPointHypothesis, [
            VALUE_KEY,
            DATE_KEY,
        ]),
        indexRaiseHypothesis: pick(hypothesis?.indexRaiseHypothesis, [
            VALUE_KEY,
            DATE_KEY,
        ]),
        rateCnraclHypothesis: pick(hypothesis?.rateCnraclHypothesis, [
            VALUE_KEY,
            DATE_KEY,
        ]),
    });
    const isDataLoading =
        isFreeHypothesisLoading || isHrSimulationResponseFetching;
    const [userChanges, setUserChanges] = useState<UserChanges>(() =>
        pickHypothesis(hrSimulationPayConstantResponse),
    );
    const { editFreeHypothesis } = useFreeHypothesisCRUDMutations({
        hrSimulationId: hrSimulation.id,
    });

    useEffect(() => {
        // We initialize the hypothesis with undefined values, if we want it to be updated once the
        // the query has returned values, we need to have thus use effect
        setUserChanges(() => pickHypothesis(hrSimulationPayConstantResponse));
    }, [hrSimulationPayConstantResponse]);

    const [isChanged, setIsChanged] = useState(false);
    const updateEditedFreeHypothesisPayload = (
        hypothesis: FreeHypothesisEditMutationParams,
    ) => {
        const index = freeHypothesisUpdatePayload.findIndex(
            (item) => item.id === hypothesis.id,
        );
        // if the hypothesis already exists, we update it else we add it
        if (index !== -1) {
            freeHypothesisUpdatePayload[index] = {
                ...freeHypothesisUpdatePayload[index],
                ...hypothesis,
            };
        } else {
            setFreeHypothesisUpdatePayload([
                ...freeHypothesisUpdatePayload,
                hypothesis,
            ]);
        }
    };

    const handleApplyChanges = () => {
        if (!indexRaiseHypothesis) {
            delete userChanges.indexRaiseHypothesis;
        }
        const [isValid, invalidKeys, allDefined] =
            validateUserChanges(userChanges);
        if (isValid && allDefined) {
            putPayConstantMutation.mutate({
                ...userChanges,
                changedHypotheses,
                ifse: freeHypothesisUpdatePayload,
            } as unknown as HrSimulationPayConstantPatchPayload);

            freeHypothesisUpdatePayload.forEach((hyp) => {
                // the edit mutation is triggered only if the hypothesis exists
                if (freeHypothesisData.some((item) => item.id === hyp.id))
                    editFreeHypothesis({
                        id: hyp.id,
                        filters: hyp.filters,
                        operation: hyp.operation,
                        newValue: hyp.newValue,
                        valueType: hyp.valueType,
                        agentsNumber: hyp.agentsNumber,
                    });
            });
            setIsChanged(false);
            setFreeHypothesisUpdatePayload([]);
            setChangedHypotheses([]);
        } else {
            console.error(
                `Invalid values for the following fields: ${invalidKeys.join(
                    ', ',
                )}`,
            );
        }
    };

    const handleUserChanges = useCallback(
        (
            hypothesisKey: HypothesisKey,
            key: ValueKey,
            newValue: number | string,
        ) => {
            setUserChanges((prev) => {
                const newUserChanges = {
                    ...prev,
                    [hypothesisKey]: {
                        ...prev[hypothesisKey],
                        [key]: newValue,
                    },
                };

                if (!indexRaiseHypothesis) {
                    delete newUserChanges.indexRaiseHypothesis;
                }

                const [isValid] = validateUserChanges(newUserChanges);
                setIsChanged(isValid);
                return newUserChanges;
            });
        },
        [],
    );

    const handleChange = (
        hypothesisKey: HypothesisKey,
        valueKey: ValueKey,
        newValue: number | string, // accept both number and string
    ) => {
        handleUserChanges(hypothesisKey, valueKey, newValue);
    };

    const createHypothesisEntryForm = (
        hypothesisKey: HypothesisKey,
        defaultValue: number | undefined,
        title: string,
        inputProps: object,
        typeValue: 'number' | 'percentage',
        totalCosts: number,
        costPerYear: CostPerYearT,
    ) => {
        const currentHypothesis = userChanges[hypothesisKey] ?? {};
        return (
            <StyPaper>
                <HypothesisEntryForm
                    title={title}
                    typeValue={typeValue}
                    isLoading={isHrSimulationPayConstantLoading}
                    defaultValue={defaultValue}
                    value={currentHypothesis?.value}
                    date={currentHypothesis?.date}
                    onChange={(newValue) => {
                        handleChange(hypothesisKey, VALUE_KEY, newValue);
                        setChangedHypotheses([
                            ...changedHypotheses,
                            hypothesisKey,
                        ]);
                    }}
                    onDateChange={(newDate) =>
                        handleChange(hypothesisKey, DATE_KEY, newDate)
                    }
                    totalCost={totalCosts}
                    costPerYear={costPerYear}
                    isHrSimulationResponseFetching={isDataLoading}
                    inputProps={inputProps}
                />
            </StyPaper>
        );
    };
    const createSmicHypothesisEntryForm = (
        defaultValue: number | undefined,
        title: string,
        inputProps: object,
        totalCosts: SmicTotalData,
        costPerYear: SmicValuesPerYear,
    ) => {
        const hypothesisKey = 'smicHypothesis';
        const currentHypothesis = userChanges[hypothesisKey] ?? {};

        return (
            <StyPaper>
                <SmicHypothesisEntryForm
                    title={title}
                    hrSimulation={hrSimulation}
                    isLoading={isHrSimulationPayConstantLoading}
                    defaultValue={defaultValue}
                    value={currentHypothesis?.value}
                    date={currentHypothesis?.date}
                    onChange={(newValue) => {
                        handleChange(hypothesisKey, VALUE_KEY, newValue);
                        setChangedHypotheses([
                            ...changedHypotheses,
                            hypothesisKey,
                        ]);
                    }}
                    onDateChange={(newDate) =>
                        handleChange(hypothesisKey, DATE_KEY, newDate)
                    }
                    totalCost={totalCosts}
                    costPerYear={costPerYear}
                    isHrSimulationResponseFetching={isDataLoading}
                    inputProps={inputProps}
                />
            </StyPaper>
        );
    };

    const createIndexRaiseHypothesisEntryForm = (
        hypothesisKey: HypothesisKey,
        defaultValue: number | undefined,
        title: string,
        inputProps: object,
        typeValue: 'number' | 'percentage',
        totalCosts: number,
        costPerYear: CostPerYearT,
    ) => {
        const currentHypothesis = userChanges[hypothesisKey] ?? {};

        return (
            <StyPaper>
                <IndexRaiseHypothesisEntryForm
                    title={title}
                    isLoading={isHrSimulationPayConstantLoading}
                    value={currentHypothesis?.value}
                    date={currentHypothesis?.date}
                    totalCost={totalCosts}
                    costPerYear={costPerYear}
                    isHrSimulationResponseFetching={isDataLoading}
                    inputProps={inputProps}
                />
            </StyPaper>
        );
    };
    const createNewHypothesisEntryForm = (
        freeHypothesisTotalCost: Record<string, number>,
        freeHypothesisPerYearCost: Record<string, CostPerYearT>,
    ) => (
        <StyPaper sx={{ minHeight: '13rem', width: '100%' }}>
            {isDataLoading ? (
                <Skeleton
                    sx={{ height: '100%' }}
                    variant="rectangular"
                    animation="wave"
                />
            ) : (
                <FreeHypothesisEntryForm
                    hrSimulation={hrSimulation}
                    hasChange={() => {
                        setIsChanged(true);
                    }}
                    freeHypothesisData={freeHypothesisData}
                    freeHypothesisTotal={freeHypothesisTotalCost}
                    freeHypothesisPerYear={freeHypothesisPerYearCost}
                    setFreeHypothesisUpdatePayload={
                        updateEditedFreeHypothesisPayload
                    }
                />
            )}
        </StyPaper>
    );
    return (
        <StyHrCard
            title={t('humanResources.simulation.payConstantHypothesis')}
            cardHeaderActions={
                <ApplyChangesButton
                    isLoading={isLoadingPayConstantMutation}
                    isDisabled={!isChanged}
                    onClick={handleApplyChanges}
                    title={t('humanResources.simulation.applyEvolution')}
                />
            }
        >
            <Grid container sx={{ minHeight: '30rem' }} spacing={2}>
                {!isProjection && (
                    <Grid item xs={2}>
                        <StyPaper>
                            <EchelonsOverview
                                hrSimulation={hrSimulation}
                                isHrSimulationResponseFetching={isDataLoading}
                                echelonTotalCost={echelonTotalCost}
                                echelonPerYear={echelonPerYear}
                                isProjection={isProjection}
                            />
                        </StyPaper>
                    </Grid>
                )}
                <Grid item xs={isProjection ? 12 : 10}>
                    <Grid container spacing={2} sx={{ minHeight: '30rem' }}>
                        {isProjection && (
                            <Grid item xs={6}>
                                <StyPaper>
                                    <EchelonsOverview
                                        hrSimulation={hrSimulation}
                                        isHrSimulationResponseFetching={
                                            isDataLoading
                                        }
                                        echelonTotalCost={echelonTotalCost}
                                        echelonPerYear={echelonPerYear}
                                        isProjection={isProjection}
                                    />
                                </StyPaper>
                            </Grid>
                        )}
                        <Grid item xs={6}>
                            {createHypothesisEntryForm(
                                'indexPointHypothesis',
                                indexPointHypothesis?.defaultValue,
                                'humanResources.simulation.valuePoint',
                                { step: 0.1, min: 0 },
                                'number',
                                indexPointTotalCost,
                                indexPointPerYear,
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            {createSmicHypothesisEntryForm(
                                smicHypothesis?.defaultValue,
                                'humanResources.simulation.valueSmic',
                                { step: 1, min: 0 },
                                smicTotalCost,
                                smicPerYear,
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            {createHypothesisEntryForm(
                                'rateCnraclHypothesis',
                                rateCnraclHypothesis?.defaultValue,
                                'humanResources.simulation.rateCNRACL',
                                { step: 0.1, min: 0 },
                                'percentage',
                                rateCnraclTotalCost,
                                rateCnraclPerYear,
                            )}
                        </Grid>

                        {indexRaiseHypothesis && (
                            <Grid item xs={6}>
                                {createIndexRaiseHypothesisEntryForm(
                                    'indexRaiseHypothesis',
                                    indexRaiseHypothesis?.defaultValue,
                                    'humanResources.simulation.freeHypothesis.title',
                                    { step: 0.1, min: 0 },
                                    'number',
                                    indexRaiseTotalCost,
                                    indexRaisePerYear,
                                )}
                            </Grid>
                        )}
                        <Grid item xs={6}>
                            {createNewHypothesisEntryForm(
                                freeHypothesisTotals,
                                freeHypothesisPerYear,
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </StyHrCard>
    );
};

export default memo(PayConstants);
